/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { PureComponent } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    shape, string, number, arrayOf, object,
} from 'prop-types';
import Typography from '@material-ui/core/Typography';
import ReactMarkdown from 'react-markdown';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import { UIDReset, UIDConsumer } from 'react-uid';
import PrintIcon from '@material-ui/icons/Print';
import { Helmet } from 'react-helmet';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import GraphqlBreadcrumbs from '../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import ResponsiveImage from '../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import HeroSingle from '../../ContentTypeComponents/HeroSingle/HeroSingle';

const styles = () => ({
    title: {
        '@media screen and (max-width: 601px)': {
            fontSize: '1.8em',
        },
    },
    details: {
        display: 'flex',
        padding: 6,
    },
    recipeContainer: {
        maxWidth: '980px',
        width: '98%',
        margin: '0 auto 40px',
    },
    recipeCopy: {
        width: '90%',
        margin: '0 auto',
    },
    descriptions: {
        margin: '5% 0',
        padding: '0 1%',
    },
    italic: {
        fontStyle: 'italic',
    },
    instructions: {
        padding: '5%',
        '@media screen and (min-width: 601px)': {
            borderLeft: 'solid 1px #ccc',
        },
    },
    ingredients: {
        padding: '5%',
    },
    pointer: {
        cursor: 'pointer',
    },
    printIcon: {
        fontSize: '1.2em', marginBottom: '-3px',
    },
    time: {
        fontSize: '.8em',
    },
    breadCrumb: {
        margin: '10px 0',
    },
    altImageContainer: {
        justifyContent: 'center',
        margin: '20px 0',
    },
    altImgge: {
        width: '100%',
        padding: '10px 6px',
    },
});

const dietMap = {
    DiabeticDiet: 'Diabetic Friendly',
    GlutenFreeDiet: 'Gluten-Free',
    HalalDiet: 'Halal',
    HinduDiet: 'Hindu',
    KosherDiet: 'Kosher',
    LowCalorieDiet: 'Low Calorie',
    LowFatDiet: 'Low Fat',
    LowLactoseDiet: 'Low Lactose / Lactose-Free',
    LowSaltDiet: 'Low Salt',
    VegetarianDiet: 'Vegetarian',
    VeganDiet: 'Vegan',
};

const getDiet = (arr) => {
    if (!arr) {
        return '';
    }
    const dietArr = [];
    try {
        arr.forEach((i) => dietArr.push(dietMap[i]));
        return dietArr.join(', ');
    } catch (err) {
        return '';
    }
};
const parseTime = (time) => {
    if (!time) {
        return '';
    }
    try {
        /*
         * Converts ISO format (for SEO Schema) to readable presentation
         * e.g. "PT15M" -> "15 minutes"
         */
        const timeParsed = time.toUpperCase().replace('P', '').replace('T', '').replace(/(H)/g, ' hours-')
            .replace(/(M)/g, ' minutes-')
            .split('-')[0];
        /*
         * Remove leading 0 from ISO time
         * e.g. "09:00" -> "9:00"
         */
        if (timeParsed.charAt(0) === '0') {
            timeParsed.replace('0', '');
        }
        return timeParsed;
    } catch (err) {
        return 'n/a';
    }
};

class Entry extends PureComponent {
    generateRecipeSchema = () => {
        const {
            recipeData: {
                recipe_instructions = '',
                cook_time = '',
                image_main = '',
                date_published = '',
                prep_time = '',
                keywords = '',
                suitable_for_diet = '',
                recipe_category = '',
                title = '',
                author = '',
                yield: recipeYield = '',
                long_description = '',
                recipe_ingredients = '',
            },
        } = this.props;
        return (
            <Helmet>
                <script type="application/ld+json">
                    {`
                    {
                        "@context": "https://schema.org",
                        "@type": "Recipe",
                        "name": "${title}",
                        "author": {
                            "@type": "Person",
                            "name": "${author}"
                        },
                        "image": "${image_main?.url}",
                        "datePublished": "${date_published}",
                        "recipeYield": "${recipeYield}",
                        "description": ${JSON.stringify(`${long_description}`)},
                        "recipeIngredient": ${JSON.stringify(recipe_ingredients)},
                        "recipeInstructions": ${JSON.stringify(recipe_instructions)},
                        "recipeCategory": ${JSON.stringify(recipe_category)},
                        "suitableForDiet": ${JSON.stringify(suitable_for_diet)},
                        "keywords": ${JSON.stringify(keywords)},
                        "cookTime": "${cook_time}",
                        "prepTime": "${prep_time}"
                    }
                `}
                </script>
            </Helmet>
        );
    };

    getBreadcrumbs = () => {
        const { recipeData: { title }, page } = this.props;
        const breadCrumb = [{
            title: 'Home',
            href: '/',
        },
        {
            title,
            href: page?.path,
        }];

        return breadCrumb;
    }

    render() {
        const { recipeData, classes } = this.props;
        if (!recipeData) return null;
        // set alternate image grid based on length
        const setGrid = recipeData.image_alt?.length === 1 ? 6 : 4;
        const seo = recipeData.seo || {};
        const mainImageStyle = recipeData.main_image_styles || {};

        // get width and height for main image from CMS
        let imageStyle = {};
        const unit = mainImageStyle?.unit === 'percent' ? '%' : 'px';  // Image render base on pixel and percent
        imageStyle = {
            width: mainImageStyle?.width ? `${mainImageStyle?.width}${unit}` : '',
            height: mainImageStyle?.height ? `${mainImageStyle?.height}${unit}` : '',
        };

        const isRecipeData = (recipeData.author?.length > 0 || recipeData.recipe_category?.length > 0 || recipeData.recipe_cuisine?.length > 0);
        const recipeCategory = recipeData.recipe_category ? ` | ${recipeData.recipe_category?.join(', ')} |` : '';
        const recipeCuisine = recipeData.recipe_cuisine ? recipeData.recipe_cuisine?.join(', ') : '';
        const recipeAuthor = recipeData.author ? recipeData.author : '';

        return (
            <>
                <GraphqlSeoHeadTags seoData={seo} />
                {this.generateRecipeSchema()}
                <div data-container="Recipe" className={classes.recipeContainer}>
                    <div className={classes.breadCrumb}>
                        <GraphqlBreadcrumbs breadCrumbArray={this.getBreadcrumbs()} />
                    </div>
                    <div className="heroImage" data-heroimage>
                        {recipeData.image_main?.url
                            ? (<ResponsiveImage className={classes.heroImg} style={imageStyle} path={recipeData.image_main?.url} alt={recipeData.title} params={{}} />)
                            : (<br />)}
                    </div>
                    {recipeData.hero_single && <HeroSingle data={recipeData.hero_single} />}
                    <div className={classes.recipeCopy} data-print="print">

                        {recipeData.title && <Typography variant="h4" component="h1" className={classes.title}>{recipeData.title}</Typography>}
                        { isRecipeData && (
                            <div className={classes.details}>
                                <span>{recipeAuthor}</span>
                                <span>{recipeCategory}</span>
                                <span>{recipeCuisine}</span>
                            </div>
                        )}
                        <div className={classes.descriptions}>
                            <Typography>
                                {recipeData.short_description && <span className={classes.italic} style={{ fontStyle: 'italic' }}><ReactMarkdown source={recipeData.short_description} escapeHtml={false} /></span>}
                                {recipeData.long_description && <ReactMarkdown source={recipeData.long_description} escapeHtml={false} />}
                            </Typography>
                        </div>

                        <Card variant="outlined">
                            <CardContent>
                                {recipeData.title && <Typography variant="h5" component="h2">{recipeData.title}</Typography>}

                                <div className={classes.time}>
                                    {recipeData.prep_time  && <span>Prep Time: {parseTime(recipeData.prep_time)} | </span>}
                                    {recipeData.cook_time  && <span>Cook Time: {parseTime(recipeData.cook_time)} | </span>}
                                    {recipeData.total_time && <span>Total Time: {parseTime(recipeData.total_time)}</span>}
                                </div>
                                {recipeData.suitable_for_diet && <div className={classes.italic}>{getDiet(recipeData.suitable_for_diet)}</div>}
                                <Grid container alignItems="flex-end" justify="flex-end" direction="column">
                                    { /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */ }
                                    <div onClick={typeof window !== 'undefined' ? window.print : null} role="link" tabIndex={0} className={classes.pointer}><PrintIcon className={classes.printIcon}>print</PrintIcon> Print</div>
                                </Grid>
                                <Grid container>
                                    <Grid item md={5} xs={12}>
                                        <div className={classes.ingredients}>
                                            <h3>Ingredients</h3>
                                            <ReactMarkdown source={recipeData.recipe_ingredients} />
                                        </div>
                                    </Grid>
                                    <Grid item md={7} xs={12}>
                                        <div className={classes.instructions}>
                                            <h3>Instructions</h3>
                                            <ReactMarkdown source={recipeData.recipe_instructions} />
                                        </div>
                                    </Grid>

                                </Grid>
                                <Grid container alignItems="flex-end" justify="flex-end" direction="column">
                                    {recipeData.recipe_yield && <div>Makes: {recipeData.recipe_yield}</div>}
                                    {recipeData.yield && <div>Serves: {recipeData.yield }</div>}
                                    {recipeData.calories && <div>Calories/Serving: {recipeData.calories}</div>}
                                </Grid>
                            </CardContent>
                        </Card>
                        {recipeData.image_alt?.length > 0 &&  (
                            <Grid container className={classes.altImageContainer}>
                                <UIDReset>
                                    <UIDConsumer>
                                        {(id, uid) => recipeData.image_alt?.map(
                                            (altImage) => (
                                                <Grid key={uid(altImage)} item xs={12} lg={setGrid}>
                                                    <ResponsiveImage className={classes.altImgge} path={altImage?.url} alt={altImage?.title} params={{}} />
                                                </Grid>
                                            ))}
                                    </UIDConsumer>
                                </UIDReset>
                            </Grid>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

Entry.propTypes = {
    page: shape({
        path: string.isRequired,
    }).isRequired,
    recipeData: shape({
        author: string,
        blog_url: string,
        cook_time: string,
        keywords: arrayOf(string.isRequired),
        long_description: string,
        nutrition: string,
        prep_time: string,
        recipe_category: arrayOf(string.isRequired),
        recipe_cuisine: arrayOf(string.isRequired),
        recipe_ingredients: string,
        recipe_instructions: string,
        recipe_yield: string,
        seo: shape({
            page_title: string,
            meta_description: string,
            keywords: string,
            canonicalUrl: string,
        }),
        short_description: string,
        skus: arrayOf(number.isRequired),
        suitable_for_diet: arrayOf(string.isRequired),
        title: string,
        total_time: string,
        image_main: arrayOf(shape({
            filename: string,
            title: string,
            url: string,
        })),
        image_alt: arrayOf(shape({
            title: string,
            url: string,
        })),
        yield: string,
    }).isRequired,
    classes: object.isRequired,
};

export default withStyles(styles)(Entry);
